import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactMenu,
  CompactTitle,
  DataTable,
  HelpPopover,
  PageHeader,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  WorkControls,
  RowOpens,
  PaneControl
} from '@/design';
import { idTypeLabel, type RegistrationRunStatus } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../api/oversight-contracts';
import type {
  IAdminRegistrationRow,
  IRegistrationQuery,
  RegistrationCard,
  RegistrationFacetField,
  RegistrationFlag,
  RegistrationSortKey
} from '../api/participant-contracts';
import { participantsApi } from '../api/participants-api';
import { countOf } from '../lists';
import { LoadError, PastTheEnd, useAdminData } from '../components/admin-page';
import { useListOrigin } from '../components/list-origin';
import { RowNameClicks } from '../components/row-name-click';
import { CardLink, recordAccountPath, type IListOriginItem } from '../components/participant-link';
import { optionLabel, type IFilterOption } from '../components/record-filters';
import { personRunLabel, RunStatusBadge } from '../components/run-status';
import { SignalChips, StateCards, type IStateSignal } from '../components/state-cards';
import { UnverifiedBadge } from '../components/unverified-badge';
import { refusalMessage } from '../refusal';
import { FunnelHeader, ParamFunnel, SortHeader } from './compliance/queue-headers';
import { useOwnColumns } from './lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  useWorkPaging
} from './lists/work-page/pager';
import { FacetChoice, RecordSearch } from './record-lists/record-work-page';
import {
  REGISTRATION_CARDS,
  REGISTRATION_SIGNALS,
  cardOf,
  flagSaid,
  flagsOf,
  useRegistrationCounts,
  useRegistrationsVisit
} from './registrations/card-signals';
import { ParamChip } from './sec-adv/firm-filters';

/**
 * Registration attempts — `GET /api/v1/admin/registrations` (SR-213's A6), under
 * `responses.browse`, so staff and admin alike. Every self-registration run, the ones that
 * resolved nobody included: an attempt that stopped at "is this you?" or was refused leaves no
 * account, no participant and no response, so this list is the only place it can be read. It is
 * the person hub's complement — a run that named somebody links to that participant's hub, and
 * one that named nobody is only ever here.
 *
 * **A work page of the full form** (SR-515, `docs/superpowers/specs/2026-10-28-work-pages-design.md`)
 * of the work-item-backed family: every card, signal, filter and order is a query parameter of
 * the route, the headers' funnels and the filter line's chips written here over those keys. The
 * cards — All, Needs attention, Refused, Completed (`card=`) — are survey-api's disjoint split of
 * the attempts; their signals, new since the viewer's last visit (`flag=new` with `since`, the
 * visit kept on this browser, `registrations/card-signals.ts`) and stuck at "is this you?"
 * (`flag=stuck`), narrow the list to the attempts raising any pressed. The counts are one read
 * (`/registrations/counts`) under the search and the filters, never the card, the flags, the sort
 * or the window.
 *
 * **No default filter.** The page opens on All, newest started first: an attempt that completed is
 * not hidden from a reader who did not know to ask for it, and Needs attention is a card the
 * reader opens rather than one the page opens for them — a preselected one would be a query the
 * address bar does not show.
 *
 * **The list is the URL** — `q`, `card`, `flag`, `survey`, `step`, `status`, `sort` and the window
 * (`page`, `size`) — so a queue can be pasted and the browser's way back from a hub returns to it.
 * A funnel is single-valued; an old address repeating `status` is still sent whole and said as one
 * chip. A value the URL names that this build does not know is still sent, and survey-api's 400
 * says so. The last answer stays, dimmed, while the next is out.
 *
 * **It renders the row and decides nothing about it.** A refusal takes two members to read, and
 * both are shown as the wire spells them: `abandoned` beside `resolution: number_taken` is a
 * number somebody already holds — the anchor step ends the run there — while `refusal` is the
 * completion's, and leaves the run `in_progress` for another address. Neither reached the
 * registrant. On a taken number the participant is the holder, named here and nowhere the
 * registrant can see. A team whose answers sent it to the RIA firm survey reads `TEAMS →
 * RIA_FIRM` on step `redirected`, abandoned and naming nobody: survey-api made nothing. The cards
 * and "stuck" are survey-api's reading of the stored state; nothing here derives them from a row,
 * and Last activity is there for a reader to judge by.
 *
 * **No action on a row.** Verifying a number is the hub's, behind its confirmation — a second
 * button here would be a second place an irreversible stamp is pressed from, and the row's link
 * to the hub is one click. Nothing polls either: a queue is read, not watched.
 */
export function AdminRegistrationsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const card = cardOf(params.get('card'));
  const flags = flagsOf(params);
  const sort = params.get('sort');
  const visit = useRegistrationsVisit();

  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const query = registrationsQueryOf(params, visit.since);
  const asked = JSON.stringify([query, page, perPage, pages]);
  const filtered = FILTERS.some((name) => params.getAll(name).some((value) => value !== ''));
  const { data, error, reading, reload } = useAdminData<IAnswered>(
    async (signal) => {
      try {
        return {
          asked,
          searched: query.q !== undefined,
          filtered,
          card,
          flags,
          page: await readItemPages(
            (at, pageSize) => participantsApi.listRegistrations({ ...query, page: at, pageSize }, signal),
            page,
            perPage,
            pages,
            WORK_ITEMS_MAX_PAGE
          )
        };
      } catch (listError) {
        // survey-api's `problems[]` say which parameter is wrong and why; its title does not.
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed. An error
  // stands only while no newer read is out.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const counts = useRegistrationCounts(query, visit.visited);
  const readAt = useReadAt(data);

  /** A change of search or filter starts at the top — made on the address as it is when it
   *  lands, so the search's delayed write never undoes a filter set meanwhile. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  // Sending the query already on screen asks again.
  const search = (text: string) => {
    if (text === q) refresh.onClick();
    else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  };
  const choose = (name: string) => (value: Nullable<string>) =>
    update((next) => (value === null || value === '' ? next.delete(name) : next.set(name, value)));
  const setFlags = (next: URLSearchParams, kept: readonly string[]) => {
    next.delete('flag');
    for (const flag of kept) next.append('flag', flag);
  };

  // With none named, the list is newest started first.
  const sortKey = (sort?.replace(/:desc$/, '') ?? 'started') as RegistrationSortKey;
  const descending = sort === null || sort.endsWith(':desc');
  // A press on the order in force turns it round; on another column, sorts by it newest first.
  const sortBy = (key: RegistrationSortKey) =>
    update((next) => next.set('sort', sortKey === key && descending ? key : `${key}:desc`));

  const clearAll = () =>
    setParams((current) => {
      const next = new URLSearchParams();
      for (const name of KEPT) {
        const value = current.get(name);
        if (value !== null) next.set(name, value);
      }
      return next;
    });

  // The funnels, held here so a filter chip's press opens its column's.
  const [funnelOpen, setFunnelOpen] = React.useState<Nullable<string>>(null);
  const saidOf = (funnel: IRegistrationFunnel): Nullable<string> => {
    const values = params.getAll(funnel.param).filter((value) => value !== '');
    return values.length === 0
      ? null
      : values.map((value) => optionLabel(funnel.known, value)).join(', ');
  };
  const funnelBody = (funnel: IRegistrationFunnel) => (
    <FacetChoice
      label={funnel.label}
      heading={false}
      typed={false}
      base={JSON.stringify([funnel.param, query])}
      read={(prefix, signal) => participantsApi.registrationFacets(funnel.param, query, prefix, signal)}
      value={params.get(funnel.param) || null}
      known={funnel.known}
      onChoose={choose(funnel.param)}
    />
  );
  const funnelOf = (funnel: IRegistrationFunnel) => (
    <ParamFunnel
      code={funnel.column}
      label={funnel.label}
      said={saidOf(funnel)}
      open={funnelOpen === funnel.column}
      onOpenChange={(open) => setFunnelOpen(open ? funnel.column : null)}
    >
      {funnelBody(funnel)}
    </ParamFunnel>
  );

  const own = REGISTRATION_COLUMNS.map((column): IDataColumn<IAdminRegistrationRow> => {
    const label = typeof column.header === 'string' ? column.header : column.id;
    const sorted = SORTS[column.id];
    const filter = FUNNELS.find((entry) => entry.column === column.id);
    const funnel = filter && funnelOf(filter);
    if (sorted) {
      const active = sortKey === sorted;
      const direction = active && !descending ? 'asc' : 'desc';
      return {
        ...column,
        label,
        header: (
          <SortHeader
            label={label}
            direction={direction}
            active={active}
            note={`${active ? 'Sorted' : 'Sort'} ${direction === 'desc' ? 'newest first' : 'oldest first'}`}
            onPress={() => sortBy(sorted)}
            funnel={funnel}
          />
        )
      };
    }
    if (funnel) return { ...column, label, header: <FunnelHeader label={label} funnel={funnel} /> };
    return column;
  });
  const chosen = useOwnColumns<IAdminRegistrationRow>('registrations');
  const columns = chosen.resolve(own);
  const columnsButton = chosen.button(own);
  // A run opens its participant's record on the Account tab beside this list — the list the
  // record's column — or in a new tab, by *Opens in* (SR-530).
  // After a write on a record opened from here, the run is looked for again under this query.
  const queryKey = JSON.stringify(query);
  const recheck = React.useMemo(
    () => ({ kind: 'registrations' as const, query: JSON.parse(queryKey) as IRegistrationQuery }),
    [queryKey]
  );
  const opening = useListOrigin('registrations', 'Registrations', originItems(shown?.page.items ?? []), recheck);

  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['attempt', 'attempts']}
                  onPage={paging.setPage}
                />
              ) : (
                emptyStatement(shown)
              )
          }
        : undefined;

  // The cards: each its count as survey-api read it, and its signals as toggles — on the open
  // card a flag added to the list or dropped, on another that card opened with the flag alone.
  const all = counts.counts?.cards.all?.count ?? null;
  const cardEntries = REGISTRATION_CARDS.map((spec) => {
    const next = dropPaging(new URLSearchParams(params));
    next.delete('flag');
    if (spec.key === 'all') next.delete('card');
    else next.set('card', spec.key);
    const current = card === spec.key;
    const read = counts.counts?.cards[spec.key];
    // Unread, the open card's count is the list's own total while no flag narrows it — the total
    // of this query, never the last one's.
    const count =
      read?.count ?? (current && flags.length === 0 && shown?.asked === asked ? shown.page.total : null);
    const signals: IStateSignal[] = read
      ? REGISTRATION_SIGNALS.flatMap((signal) => {
          const counted = read.signals[signal.key];
          if (typeof counted !== 'number') return [];
          const active = current && flags.includes(signal.key);
          return [
            {
              key: signal.key,
              label: signal.label,
              short: signal.tip ? { label: signal.label, tip: signal.tip } : undefined,
              tone: signal.tone,
              count: counted,
              active,
              toggleName: `Show ${counted.toLocaleString('en-US')} ${signal.words} in ${spec.label}`,
              onToggle: () => {
                if (current) {
                  const kept = flags.filter((flag) => flag !== signal.key);
                  update((pressed) => setFlags(pressed, active ? kept : [...kept, signal.key]));
                } else {
                  const pressed = new URLSearchParams(next);
                  pressed.append('flag', signal.key);
                  setParams(pressed);
                }
              }
            }
          ];
        })
      : [];
    return {
      key: spec.key,
      to: { search: next.toString() },
      current,
      label: spec.label,
      count: count?.toLocaleString('en-US') ?? '…',
      hint: spec.hint,
      tooltip: `${spec.label} — ${spec.hint}`,
      signals,
      share: count !== null && all && spec.key !== 'all' ? count / all : undefined,
      data: { 'data-card': spec.key },
      countData: { 'data-count': count?.toString() ?? '' }
    };
  });
  const openCard = cardEntries.find((entry) => entry.current) ?? cardEntries[0];
  const total = all ?? (card === 'all' && flags.length === 0 && shown?.asked === asked ? shown.page.total : null);

  const signalChips = (
    <SignalChips
      chips={flags.map((flag) => {
        const words = flagSaid(flag).words;
        return {
          key: flag,
          words,
          clearName: `Show every attempt, not only those ${words}`,
          onClear: () => update((next) => setFlags(next, flags.filter((one) => one !== flag)))
        };
      })}
    />
  );
  // Every filter applied, as a chip with its value — under the toolbar and on the compact bar's
  // second row: the flags pressed first, then each funnel set, whose press opens its column's.
  const appliedChips: ICompactChip[] = [
    ...(flags.length > 0 ? [{ key: 'flags', chip: signalChips }] : []),
    ...FUNNELS.flatMap((funnel) => {
      const said = saidOf(funnel);
      if (said === null) return [];
      return [
        {
          key: funnel.param,
          chip: (
            <ParamChip
              words={`${funnel.label}: ${said}`}
              body={funnelBody(funnel)}
              onPress={() => {
                const drawn = document.querySelector(`[data-funnel="${CSS.escape(funnel.column)}"]`);
                if (drawn === null) return false;
                setFunnelOpen(funnel.column);
                return true;
              }}
              onRemove={() => choose(funnel.param)(null)}
            />
          )
        }
      ];
    })
  ];
  const toolbar = useWorkToolbar();

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  const refresh = {
    reading: reading || counts.reading,
    readAt,
    onClick: () => {
      void reload();
      void counts.reload();
    }
  };
  const help = <AboutRegistrations />;
  const searchBox = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Filter registrations"
      placeholder="Filter registrations…"
      onSearch={search}
      compact={compact}
    />
  );

  return (
    <>
      <PageHeader
        title="Registrations"
        row
        actions={
          <WorkPageHeaderActions
            summary={total === null ? null : countOf(total, 'attempt')}
            summaryData={{ 'data-registrations-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Registrations, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      >
        <CompactTitle>Registrations</CompactTitle>
        <CompactMenu
          label="Show"
          chip
          trigger={
            <>
              {openCard.label}
              <span className="font-semibold tabular-nums">{openCard.count}</span>
            </>
          }
          items={cardEntries.map((entry) => ({
            key: entry.key,
            label: entry.label,
            to: entry.to,
            current: entry.current,
            count: entry.count
          }))}
        />
        <CompactDivider />
        {searchBox(true)}
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <StateCards label="Show" compact cards={cardEntries} />

        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={searchBox(false)}
            quick={null}
            quickCount={0}
            end={
              <>
                {columnsButton}
                <PaneControl prefs={opening.prefs} kind="record" />
              </>
            }
          />
          {appliedChips.length > 0 && (
            <CompactChipLine label="Filters" chips={appliedChips} onClearAll={clearAll} />
          )}
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}

        <RowNameClicks accept={opening.names} open={opening.beside}>
        <DataTable
          label="Registrations"
          columns={[openColumn(opening.beside), ...columns]}
          rows={tableState ? null : (shown?.page.items ?? null)}
          rowKey={(run) => run.runId}
          onOpen={(run) => run.participant && opening.open(recordAccountPath(run.participant.participantId))}
          busy={reading && shown !== null}
          state={tableState}
          minWidth="56rem"
          stickyTop={STICKY_TABLE_TOP}
        />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** The "?": what the page holds, what each card and signal means, and how the controls read. */
function AboutRegistrations() {
  return (
    <HelpPopover label="About Registrations" align="end">
      <p>
        Every self-registration attempt, newest first — the ones that named nobody included. A number
        somebody already holds ends the run: <code>abandoned</code>, resolved as{' '}
        <code>number_taken</code>, and the participant is who holds it. A refused address leaves the
        run <code>in_progress</code>, because an address can be changed. The registrant was told
        neither.
      </p>
      <ul className="grid list-disc gap-1 pl-5">
        {REGISTRATION_CARDS.map((card) => (
          <li key={card.key}>
            <span className="font-medium">{card.label}</span> — {card.about}
          </li>
        ))}
      </ul>
      <p>A card&apos;s badges count its attempts by signal:</p>
      <ul className="grid list-disc gap-1 pl-5">
        {REGISTRATION_SIGNALS.map((signal) => (
          <li key={signal.key}>
            <span className="font-medium">{signal.words}</span> — {signal.about}
          </li>
        ))}
      </ul>
      <p>Press badges to show the attempts raising any of them; the counts stay as they are.</p>
      <p>
        The search reads as you type: every word is found in the number typed, the registry&apos;s
        name, its firm&apos;s name, the participant&apos;s name or the account&apos;s email.
      </p>
      <p>
        Started and Last activity sort, a press again the other way. A column&apos;s funnel narrows
        the list to one of its values, counted among the attempts the other filters keep. Clear all
        keeps the card and the order. Nothing derives &ldquo;stuck&rdquo; from Last activity:
        survey-api&apos;s sweep decides that.
      </p>
      <p>
        A run opens its participant’s Account tab beside this list by its first icon — the list
        stays beside it, ↑ and ↓ walk it, Esc comes back — and in a new tab by ↗; a click on the row
        or the name goes where «Opens in» says.
      </p>
    </HelpPopover>
  );
}

/** The URL's filters, as survey-api's query — every value sent as spelled. */
function registrationsQueryOf(params: URLSearchParams, since: Nullable<string>): IRegistrationQuery {
  const card = cardOf(params.get('card'));
  return {
    q: params.get('q')?.trim() || undefined,
    card: card === 'all' ? undefined : (card as RegistrationCard),
    flag: flagsOf(params) as RegistrationFlag[],
    since: since ?? undefined,
    status: params.getAll('status').filter((value) => value !== '') as RegistrationRunStatus[],
    survey: params.get('survey') || undefined,
    step: params.get('step') || undefined,
    sort: params.get('sort') || undefined
  };
}

/** The list's filter keys — what makes an empty list "none matches" rather than "none yet". */
const FILTERS = ['survey', 'step', 'status'];

/** The keys that say where the reader is rather than what narrows: Clear all keeps them. */
const KEPT = ['card', 'sort'];

/** The sortable columns, by id: survey-api's key, each newest first on its first press. */
const SORTS: Partial<Record<string, RegistrationSortKey>> = {
  started: 'started',
  'last-activity': 'last_activity'
};

interface IRegistrationFunnel {
  column: string;
  param: RegistrationFacetField;
  label: string;
  /** What the funnel offers where the facets cannot be read. */
  known: readonly IFilterOption[];
}

const spelled = (codes: readonly string[]): IFilterOption[] =>
  codes.map((code) => ({ value: code, label: code }));

const FUNNELS: readonly IRegistrationFunnel[] = [
  {
    column: 'survey',
    param: 'survey',
    label: 'Survey',
    known: [...spelled(['FA', 'FSP', 'TEAMS', 'RIA_FIRM']), { value: 'person', label: 'Person' }]
  },
  {
    column: 'step',
    param: 'step',
    label: 'Step',
    known: spelled(['anchor', 'confirm_identity', 'route', 'complete', 'redirected'])
  },
  {
    column: 'status',
    param: 'status',
    label: 'Status',
    known: spelled(['in_progress', 'completed', 'abandoned'])
  }
];

/** A page of runs, with the query it answers and what narrowed it. */
interface IAnswered {
  asked: string;
  searched: boolean;
  filtered: boolean;
  card: string;
  flags: readonly string[];
  page: IPage<IAdminRegistrationRow>;
}

/** Says what narrowed the list, so "none matches" is never read as "none is held" (rule 3). */
function emptyStatement(answer: IAnswered): string {
  const label = REGISTRATION_CARDS.find((entry) => entry.key === answer.card)?.label;
  const No = answer.card === 'all' ? 'No registration attempt' : `No attempt in ${label}`;
  if (answer.flags.length > 0) {
    const none = answer.flags.map((flag) => flagSaid(flag).none).join(' or ');
    const narrowed =
      answer.searched && answer.filtered
        ? ' matching this search and these filters'
        : answer.searched
          ? ' matching this search'
          : answer.filtered
            ? ' matching these filters'
            : '';
    return `${No}${narrowed} ${none}.`;
  }
  if (answer.searched && answer.filtered) return `${No} matches this search with these filters.`;
  if (answer.searched) return `${No} matches this search.`;
  if (answer.filtered) return `${No} matches these filters.`;
  return answer.card === 'all' ? 'No registration attempt yet.' : `${No}.`;
}

/** The people the drawn runs named, once each in the runs' order, as the column beside a record
 *  opened from them — several runs of one person open the one record. */
function originItems(rows: readonly IAdminRegistrationRow[]): IListOriginItem[] {
  const seen = new Set<string>();
  return rows.flatMap((run) => {
    if (!run.participant || seen.has(run.participant.participantId)) return [];
    seen.add(run.participant.participantId);
    return [
      {
        key: run.participant.participantId,
        to: recordAccountPath(run.participant.participantId),
        primary: run.participant.displayName,
        secondary: run.status
      }
    ];
  });
}

/** Both ways in, first on a row: the record the run named, on its Account tab — where its
 *  registration runs are — beside this list, and in a new tab; nothing on a run that named nobody.
 *  The column is none the viewer arranges or hides. */
function openColumn(beside: (to: string) => void): IDataColumn<IAdminRegistrationRow> {
  return {
    id: 'open',
    header: <span className="sr-only">Open</span>,
    label: 'Open',
    className: 'w-0 whitespace-nowrap',
    cell: ({ participant }) =>
      participant && (
        <RowOpens
          name={participant.displayName}
          onPane={() => beside(recordAccountPath(participant.participantId))}
          to={recordAccountPath(participant.participantId)}
          tabWords={`Open ${participant.displayName} — Account in a new tab`}
        />
      )
  };
}

/**
 * A run's columns: the first ones on by default, the registry's name, the attempts and the last
 * activity ones that can be taken away; the instant a run finished and its id there to be asked
 * for.
 */
const REGISTRATION_COLUMNS: IDataColumn<IAdminRegistrationRow>[] = [
  {
    id: 'started',
    header: 'Started',
    className: 'text-xs whitespace-nowrap text-muted-foreground',
    cell: (run) => formatDateTime(run.startedAt)
  },
  {
    id: 'survey',
    header: 'Survey',
    className: 'whitespace-nowrap',
    cell: (run) => (
      <>
        {/* An empty survey is a run whose state this build cannot read — survey-api keeps the
            row rather than hide the one case worth looking at. */}
        <span className="font-medium">{personRunLabel(run.numbers) ?? (run.survey || unknown)}</span>
        {run.destinationSurvey !== run.survey && (
          <span className="text-muted-foreground"> → {run.destinationSurvey || unknown}</span>
        )}
      </>
    )
  },
  {
    id: 'step',
    header: 'Step',
    className: 'font-mono text-xs',
    cell: (run) => run.step || unknown
  },
  {
    id: 'status',
    header: 'Status',
    cell: (run) => (
      <span className="grid justify-items-start gap-1">
        <RunStatusBadge status={run.status} />
        {run.resolution && <Code label="Resolved as" value={run.resolution} />}
        {run.refusal && <Code label="Refused" value={run.refusal} />}
      </span>
    )
  },
  {
    id: 'number',
    header: 'Number',
    cell: (run) =>
      run.value === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="grid gap-0.5 whitespace-nowrap">
          <span className="text-muted-foreground">
            {run.idType ? idTypeLabel(run.idType) : unknown}
          </span>
          <span className="font-mono">{run.value}</span>
          {run.lookup && <Code label="Lookup" value={run.lookup} />}
        </span>
      )
  },
  {
    // The name FINRA or the SEC returned for the number — what a reviewer holds against the
    // name the registrant typed; nothing where no registry named anybody.
    id: 'registry-name',
    header: 'Registry’s name',
    optional: true,
    cell: (run) => run.registryName ?? <span className="text-muted-foreground">—</span>
  },
  {
    id: 'attempts',
    header: 'Attempts',
    optional: true,
    numeric: true,
    cell: (run) => run.attempts
  },
  {
    id: 'participant',
    header: 'Participant',
    cell: (run) =>
      run.participant ? (
        <span className="flex flex-wrap items-center gap-2">
          {/* The record's Account tab, where ↗ and the row lead (SR-530): a Ctrl/⌘ or middle click
              opens the same. */}
          <CardLink to={recordAccountPath(run.participant.participantId)} className="break-words">
            {run.participant.displayName}
          </CardLink>
          {run.participant.unverified && <UnverifiedBadge />}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      )
  },
  {
    id: 'last-activity',
    header: 'Last activity',
    optional: true,
    className: 'text-xs text-muted-foreground',
    cell: (run) => formatDateTime(run.lastActivityAt)
  },
  {
    id: 'finished',
    header: 'Finished',
    optional: true,
    hidden: true,
    className: 'text-xs whitespace-nowrap text-muted-foreground',
    cell: (run) =>
      run.finishedAt ? formatDateTime(run.finishedAt) : <span className="text-muted-foreground">—</span>
  },
  {
    id: 'run',
    header: 'Run id',
    optional: true,
    hidden: true,
    className: 'font-mono text-xs break-all',
    cell: (run) => run.runId
  }
];

/** A stored code, labelled — read out of the run's own events, so shown as the wire spells it. */
function Code({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-xs">
      <span className="text-muted-foreground">{label} </span>
      {/* A long code breaks after its underscores, never inside a word. */}
      <span className="font-mono">
        {value.split('_').map((part, at) => (
          <React.Fragment key={at}>
            {at > 0 && (
              <>
                _<wbr />
              </>
            )}
            {part}
          </React.Fragment>
        ))}
      </span>
    </span>
  );
}

const unknown = <span className="text-muted-foreground">unknown</span>;
